// ══════════════════════════════════════════════════════════════════════════════
// PriestRegisterPage.js — Public priest application form
// Access: /?priest_register=1
// ══════════════════════════════════════════════════════════════════════════════
import { useState } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const C = {
  bg:     "#06030A", bg2: "#0D0818", bg3: "#130D22",
  saffron:"#E8832A", gold: "#C9953A", cream: "#FFF5E0",
  text:   "#EDE0C8", dim:  "#8A7558",
  border: "rgba(201,149,58,0.25)",
  glow:   "rgba(232,131,42,0.12)",
};

const SPECIALIZATIONS = [
  "Ganesh Puja", "Satyanarayan Puja", "Rudrabhishek", "Navagraha Puja",
  "Griha Pravesh", "Vivah / Wedding", "Namkaran", "Annaprashan",
  "Sundara Kanda Path", "Hanuman Puja", "Durga Puja", "Lakshmi Puja",
  "Pitru Tarpan", "Mrityunjaya Japa", "Homa / Havan",
];

const LANGUAGES = ["Telugu", "Hindi", "Sanskrit", "Tamil", "Kannada", "Malayalam", "Marathi", "English"];

function CheckPill({ label, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        padding: "6px 14px", borderRadius: "20px", fontSize: "0.82rem",
        border: checked ? `1px solid ${C.saffron}` : `1px solid ${C.border}`,
        background: checked ? "rgba(232,131,42,0.15)" : "transparent",
        color:  checked ? C.saffron : C.dim,
        cursor: "pointer", transition: "all 0.15s",
      }}
    >
      {checked ? "✓ " : ""}{label}
    </button>
  );
}

export default function PriestRegisterPage() {
  const [form, setForm] = useState({
    name:             "",
    phone:            "",
    email:            "",
    bio:              "",
    specializations:  [],
    languages:        [],
    experience_years: "",
    location:         "",
  });
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [errMsg, setErrMsg] = useState("");

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleArr = (key, val) =>
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    if (!form.name.trim())  return setErrMsg("Please enter your full name.");
    if (!form.phone.trim()) return setErrMsg("Please enter your phone number.");
    if (form.specializations.length === 0) return setErrMsg("Please select at least one specialization.");
    if (form.languages.length === 0)       return setErrMsg("Please select at least one language.");

    setState("submitting");
    try {
      const res  = await fetch(`${API}/priest/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          experience_years: parseInt(form.experience_years, 10) || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setState("success");
    } catch (e) {
      setState("idle");
      setErrMsg(e.message || "Something went wrong. Please try again.");
    }
  };

  // Success screen
  if (state === "success") {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ maxWidth: "480px", width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px", filter: "drop-shadow(0 0 20px rgba(232,131,42,0.6))" }}>🙏</div>
          <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.3rem", color: C.saffron, marginBottom: "12px" }}>
            Application Submitted
          </div>
          <div style={{ color: C.text, lineHeight: "1.8", marginBottom: "24px", fontFamily: "'Crimson Pro', serif", fontSize: "1.05rem" }}>
            Thank you for your interest in serving as a VedSanga priest.<br />
            Our team will review your application and contact you on WhatsApp within 2–3 business days.
          </div>
          <div style={{
            background: "rgba(232,131,42,0.07)", border: `1px solid ${C.border}`,
            borderRadius: "12px", padding: "16px 20px",
            fontSize: "0.85rem", color: C.dim, lineHeight: "1.8",
          }}>
            <span style={{ color: C.saffron }}>ॐ</span> &nbsp;
            सर्वे भवन्तु सुखिनः — May all beings be happy
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "40px 24px",
    }}>
      <div style={{ width: "100%", maxWidth: "600px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <div style={{ fontSize: "3rem", marginBottom: "10px", filter: "drop-shadow(0 0 16px rgba(232,131,42,0.5))" }}>🔱</div>
          <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.4rem", color: C.saffron, marginBottom: "6px" }}>
            Join as a Priest
          </div>
          <div style={{ fontSize: "0.9rem", color: C.dim, fontStyle: "italic" }}>
            VedSanga · Online Puja Platform
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Personal details */}
          <Section title="Personal Details">
            <Field label="Full Name *">
              <input
                style={inputStyle}
                placeholder="Pandit / Swami / Your full name"
                value={form.name}
                onChange={e => set("name", e.target.value)}
              />
            </Field>
            <Row>
              <Field label="WhatsApp Phone *">
                <input
                  style={inputStyle}
                  placeholder="+91 99999 99999"
                  value={form.phone}
                  onChange={e => set("phone", e.target.value)}
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={e => set("email", e.target.value)}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Years of Experience *">
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  placeholder="e.g. 10"
                  value={form.experience_years}
                  onChange={e => set("experience_years", e.target.value)}
                />
              </Field>
              <Field label="Location (City, State)">
                <input
                  style={inputStyle}
                  placeholder="e.g. Hyderabad, Telangana"
                  value={form.location}
                  onChange={e => set("location", e.target.value)}
                />
              </Field>
            </Row>
            <Field label="Bio / About You">
              <textarea
                style={{ ...inputStyle, minHeight: "80px", resize: "vertical", lineHeight: "1.6" }}
                placeholder="Brief introduction — lineage, training, your approach to puja…"
                value={form.bio}
                onChange={e => set("bio", e.target.value)}
              />
            </Field>
          </Section>

          {/* Specializations */}
          <Section title="Puja Specializations *">
            <div style={{ fontSize: "0.82rem", color: C.dim, marginBottom: "12px" }}>
              Select all puja types you can perform
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {SPECIALIZATIONS.map(s => (
                <CheckPill
                  key={s} label={s}
                  checked={form.specializations.includes(s)}
                  onToggle={() => toggleArr("specializations", s)}
                />
              ))}
            </div>
          </Section>

          {/* Languages */}
          <Section title="Languages *">
            <div style={{ fontSize: "0.82rem", color: C.dim, marginBottom: "12px" }}>
              Languages you can conduct puja in
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {LANGUAGES.map(l => (
                <CheckPill
                  key={l} label={l}
                  checked={form.languages.includes(l)}
                  onToggle={() => toggleArr("languages", l)}
                />
              ))}
            </div>
          </Section>

          {/* Requirements notice */}
          <div style={{
            background: "rgba(232,131,42,0.05)", border: `1px solid rgba(232,131,42,0.18)`,
            borderRadius: "12px", padding: "16px 20px", marginBottom: "24px",
            fontSize: "0.82rem", color: C.dim, lineHeight: "1.8",
          }}>
            <div style={{ color: C.saffron, fontFamily: "'Cinzel Decorative', serif", fontSize: "0.72rem", marginBottom: "8px", letterSpacing: "0.05em" }}>
              REQUIREMENTS TO JOIN
            </div>
            ✓ Stable internet connection (minimum 5 Mbps upload)<br />
            ✓ Two smartphones — one for altar camera, one to see devotees' faces<br />
            ✓ Flexible tripod with dual phone mount (available on Amazon India ₹500–₹1,500)<br />
            ✓ Quiet, clean space for conducting puja<br />
            ✓ Availability on agreed schedule (advance notice)<br />
            ✓ WhatsApp for coordination with VedSanga team
          </div>

          {errMsg && (
            <div style={{
              background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: "8px", padding: "10px 14px", marginBottom: "16px",
              fontSize: "0.85rem", color: "#fca5a5",
            }}>
              {errMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={state === "submitting"}
            style={{
              width: "100%", padding: "15px",
              background: state === "submitting"
                ? "rgba(232,131,42,0.3)"
                : "linear-gradient(135deg, #E8832A, #C9953A)",
              border: "none", borderRadius: "12px", color: "white",
              fontFamily: "'Cinzel Decorative', serif",
              fontSize: "0.95rem", letterSpacing: "0.04em",
              cursor: state === "submitting" ? "wait" : "pointer",
              boxShadow: state === "submitting" ? "none" : "0 6px 24px rgba(232,131,42,0.3)",
              transition: "all 0.2s",
            }}
          >
            {state === "submitting" ? "Submitting…" : "🔱 Submit Application"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: "20px", fontSize: "0.78rem", color: C.dim }}>
          Already a VedSanga priest? Contact us on WhatsApp for your dashboard link.
        </div>
      </div>
    </div>
  );
}

// ── Small layout helpers ──────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{
      background: C.bg2, border: `1px solid ${C.border}`,
      borderRadius: "16px", padding: "24px 28px", marginBottom: "20px",
    }}>
      <div style={{
        fontFamily: "'Cinzel Decorative', serif",
        fontSize: "0.78rem", color: C.saffron,
        letterSpacing: "0.06em", marginBottom: "18px",
        paddingBottom: "10px", borderBottom: `1px solid rgba(201,149,58,0.12)`,
      }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ fontSize: "0.78rem", color: C.dim, marginBottom: "6px", letterSpacing: "0.03em" }}>
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "#0D0818",
  border: "1px solid rgba(201,149,58,0.25)",
  borderRadius: "8px", padding: "10px 14px",
  color: "#EDE0C8", fontFamily: "'Crimson Pro', serif",
  fontSize: "0.95rem", outline: "none",
};
